<script lang="ts">
	import { browser } from '$app/environment';
	import { ClientVelatlasLoggerApi } from '$lib/api/velatlas/velatlas.loggerApi.client';
	import ImageLightbox from '$lib/components/ui/ImageLightbox.svelte';
	import type { ImageDetails } from '$lib/features/image/imageDetails.type';
	import { deviceScreen } from '$lib/state/deviceScreen.svelte';
	import { ANALYTICS_EVENT_NAMES } from '$lib/statics';
	import { UrlUtil } from '$lib/utils/url.util';
	import { onDestroy } from 'svelte';
	import type { Attachment } from 'svelte/attachments';
	import type { ClassValue } from 'svelte/elements';
	import IconCamera from '~icons/mdi/camera';

	const LIGHTBOX_FADING_DURATION = 200;

	const {
		classes,
		internalClasses,
		images,
		ariaLabel,
		eagerLoad = false
	}: {
		classes?: ClassValue;
		internalClasses?: ClassValue;
		images: ImageDetails[];
		ariaLabel: string;
		eagerLoad?: boolean;
	} = $props();

	const analyticsApi = new ClientVelatlasLoggerApi();

	let maxScrollPercentage = $state<number>(0);
	let highestIndexReached = $state<number>(0);
	let hasScrolled = $state<boolean>(false);
	let hasOpenedModal = $state<boolean>(false);
	const gallery = $state({
		isOpen: false,
		indexToOpen: 0
	});
	let showLightbox = $state<boolean>(false); // helps us control the fading in and out

	const minImagesRequired = $derived<number>(deviceScreen.biggerThanMobile ? 4 : 2);

	onDestroy(() => {
		if (browser && !hasOpenedModal && hasScrolled && images.length > minImagesRequired) {
			analyticsApi.sendAnalytics(
				{
					event: ANALYTICS_EVENT_NAMES.galleryScrolled,
					payload: {
						scrollPercentage: maxScrollPercentage,
						images: images.length
					}
				},
				{ keepalive: true }
			);
		}
	});

	function handleScroll(e: Event): void {
		const el = e.currentTarget as HTMLElement;
		const maxScroll = el.scrollHeight - el.clientHeight;
		if (maxScroll > 0) {
			hasScrolled = true;
			const pct = Math.min(100, Math.max(0, Math.round((el.scrollTop / maxScroll) * 100)));
			if (pct > maxScrollPercentage) {
				maxScrollPercentage = pct;
			}

			const containerBottom = el.getBoundingClientRect().bottom;
			const buttons = el.querySelectorAll<HTMLButtonElement>('button[data-gallery-index]');
			buttons.forEach((btn) => {
				const idx = Number(btn.dataset.galleryIndex);
				if (!isNaN(idx) && btn.getBoundingClientRect().top < containerBottom) {
					if (idx > highestIndexReached) {
						highestIndexReached = idx;
					}
				}
			});
		}
	}

	function onOpenLightbox(indexToOpen: number): void {
		hasOpenedModal = true;
		gallery.indexToOpen = indexToOpen;
		gallery.isOpen = true;
		// Small delay to ensure portal is mounted before transition starts, so the animation plays
		setTimeout(() => {
			showLightbox = true;
		}, 10);
	}

	function onCloseLightbox(): void {
		showLightbox = false;
		// Wait for fade out animation before actually closing
		setTimeout(() => {
			gallery.isOpen = false;
			gallery.indexToOpen = 0;
		}, LIGHTBOX_FADING_DURATION);
	}

	const portal: Attachment = (node) => {
		if (browser) {
			// append to doc body so it's always on top
			document.body.appendChild(node);
		}

		return () => {
			if (browser && node.parentNode) {
				node.remove();
			}
		};
	};
</script>

{#if gallery.isOpen}
	<div {@attach portal}>
		{#if showLightbox}
			<ImageLightbox
				{images}
				startIndex={gallery.indexToOpen}
				fadingDuration={LIGHTBOX_FADING_DURATION}
				onClose={onCloseLightbox}
			/>
		{/if}
	</div>
{/if}

<div class="group relative flex w-full flex-col {classes}">
	<div
		onscroll={handleScroll}
		class="h-[50vh] min-h-0 shrink scrollbar-thin scrollbar-thumb-base-300 scrollbar-track-transparent overflow-y-auto rounded-box pr-2 {internalClasses}"
	>
		<div class="grid grid-cols-1 gap-2 md:grid-cols-2">
			{#each images as image, i (image.wikiId)}
				<button
					type="button"
					class="group/img relative block aspect-square w-full cursor-zoom-in overflow-hidden rounded-box"
					aria-label="View image {i + 1}"
					data-gallery-index={i}
					onclick={() => onOpenLightbox(i)}
				>
					{@render Image(image, i)}
				</button>
			{/each}
		</div>
	</div>

	{@render OpenModalBtn()}
</div>

{#snippet Image(image: ImageDetails, i: number)}
	{const shouldLoadASAP = $derived<boolean>(eagerLoad && i < 3)}
	<img
		class="h-full w-full object-cover transition-transform duration-700 group-hover/img:scale-110"
		src={UrlUtil.getFullImageUrl(image)}
		alt={image.altText}
		loading={shouldLoadASAP ? 'eager' : 'lazy'}
		fetchpriority={shouldLoadASAP ? 'high' : 'auto'}
		width={image.width}
		height={image.height}
		decoding="async"
	/>
	<div class="absolute inset-0 bg-black/0 transition-colors group-hover/img:bg-black/15"></div>
{/snippet}

{#snippet OpenModalBtn()}
	<button
		type="button"
		class="btn absolute right-6 bottom-2 border-base-content/10 bg-base-100 text-base-content shadow-md backdrop-blur-md btn-neutral btn-sm hover:bg-base-200"
		aria-label={ariaLabel}
		onclick={() => onOpenLightbox(0)}
	>
		<IconCamera class="text-base text-primary" />
		<span>View Gallery ({images.length})</span>
	</button>
{/snippet}
